/**
 * Which swaps this device has exported as a share card.
 *
 * WHY IT IS LOCAL, AND ONLY LOCAL. Asking in app creates a shared row both
 * trainers can see; exporting a card is a thing that happens on ONE phone,
 * against a share sheet the app never hears back from. There is nothing to sync
 * — the friend's copy of the trade has no opinion about whether you sent them a
 * picture — so this is a device-local ledger, in the same shape as the wish list
 * store: localStorage for the fact, an event for the re-render.
 *
 * KEYED BY THE PAIRING, NOT BY A PROPOSAL. The same swap can be exported before
 * any proposal exists (straight off the Trade Match card) and again from the
 * proposal row it later becomes. Both are the same trade to a trainer, so the
 * key is what is actually changing hands — give species + variant, get species +
 * variant, oriented to the person reading — and a card exported at either point
 * marks the pairing at both.
 *
 * It is a record of what you SENT, not of what happened next: a trade stays
 * marked as card-shared whether or not the friend ever replied.
 */
import { useSyncExternalStore } from "react";

export const TRADE_SHARE_LOG_EVENT = "nexus:trade-share-log-changed";
const STORAGE_KEY = "nexus.tradeShareLog.v1";

/** One side of a swap — structurally the TradeSide the Trade Match works in. */
interface Side {
  speciesId: number;
  variant: string;
}

/** A swap, oriented to the reader: what leaves, and what arrives. */
export interface TradePairing {
  give: Side;
  get: Side;
}

/** Canonical identity for a pairing. Mirrors dexStateStore's `variant:id` shape. */
export function tradePairingKey(p: TradePairing): string {
  return `${p.give.variant}:${p.give.speciesId}>${p.get.variant}:${p.get.speciesId}`;
}

// Stable empty snapshot for the server/test render of useSyncExternalStore, and
// the module cache — getSnapshot must return the SAME reference until a
// mutation actually changes something.
const EMPTY: ReadonlySet<string> = Object.freeze(new Set<string>()) as ReadonlySet<string>;
let cache: ReadonlySet<string> | null = null;

function readRaw(): ReadonlySet<string> {
  if (typeof localStorage === "undefined") return EMPTY;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    return new Set(parsed.filter((k): k is string => typeof k === "string" && k.length > 0));
  } catch {
    return EMPTY;
  }
}

/** Every pairing this device has exported a card for. */
export function getTradeShareLog(): ReadonlySet<string> {
  if (!cache) cache = readRaw();
  return cache;
}


/**
 * Record a card export. Idempotent: exporting the same swap twice is one fact.
 *
 * A blocked or full localStorage (iOS WKWebView private mode) degrades to
 * in-memory only — the chip still appears for this session, which is better than
 * an export that leaves no trace at all.
 */
export function markSharedAsCard(pairing: TradePairing): void {
  const key = tradePairingKey(pairing);
  const current = getTradeShareLog();
  if (current.has(key)) return;
  const next = new Set(current);
  next.add(key);
  cache = next;
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      /* quota / blocked — keep the in-memory cache */
    }
  }
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new Event(TRADE_SHARE_LOG_EVENT));
    } catch {
      /* swallow */
    }
  }
}

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(TRADE_SHARE_LOG_EVENT, cb);
  return () => window.removeEventListener(TRADE_SHARE_LOG_EVENT, cb);
}

/** Live view of the log, so a chip appears the moment a card is exported. */
export function useTradeShareLog(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, getTradeShareLog, () => EMPTY);
}

/** Test seam: drop the parsed cache so the next read hits storage again. */
export function resetTradeShareLogCache(): void {
  cache = null;
}
